import {
  Autocomplete,
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  InputNumber,
  InputSize,
  InputText,
  SelectSize,
  Textarea,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../../hoc/new-application/types';
import InfoRow from '../../../common/info-row';
import useAffiliatedCompany from '../../hooks/use-affiliated-company';
import { AffilatedCompanyTypes } from '../types';

import cn from '../../../common/styles.module.css';

const AffilatedCompany = ({
  status,
  isEditable,
  affilatedCompaniesValues,
  setAffilatedCompaniesValues,
  deleteAffilatedCompanyInProgress,
  onDeleteAffilatedCompany,
  selectedAffilatedCompanyId,
  countries,
  index,
  affilatedCompany,
}: AffilatedCompanyTypes) => {
  const { expanded, setExpanded, formValues, onChange } = useAffiliatedCompany({
    affilatedCompaniesValues,
    affilatedCompany,
    setAffilatedCompaniesValues,
  });

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {`Company #${index + 1}`}
          </button>

          {isEditable &&
            status !== NewApplicationStatus.ACCEPTED &&
            status !== NewApplicationStatus.DECLINED && (
              <button
                type="button"
                className={cn.deleteAccountCurrencyButton}
                onClick={() =>
                  onDeleteAffilatedCompany(affilatedCompany?.id as string)
                }
              >
                {deleteAffilatedCompanyInProgress &&
                selectedAffilatedCompanyId === affilatedCompany?.id ? (
                  <IconLoader width={18} height={18} />
                ) : (
                  <IconTrash />
                )}
              </button>
            )}
        </div>
      </div>
      {expanded && (
        <>
          <InfoRow title="Company name" lessPadding>
            <InputText
              name={`name-${affilatedCompany?.id}`}
              placeholder="Enter name..."
              value={formValues?.name}
              size={InputSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `name-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              className="borderless"
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
            />
          </InfoRow>
          <InfoRow title="Country" lessPadding>
            <Autocomplete
              name={`countryId-${affilatedCompany?.id}`}
              placeholder="Select country..."
              list={countries}
              value={formValues?.countryId}
              size={SelectSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `countryId-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              borderless
              fullWidth
            />
          </InfoRow>
          <InfoRow title="Registration number" lessPadding>
            <InputText
              name={`registrationNumber-${affilatedCompany?.id}`}
              placeholder="Enter number..."
              value={formValues?.registrationNumber}
              size={InputSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `registrationNumber-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              className="borderless"
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
            />
          </InfoRow>
          <InfoRow title="Main activities" className={cn.noFlex} lessPadding>
            <Textarea
              name={`mainActivities-${affilatedCompany?.id}`}
              placeholder="Enter details......"
              value={formValues?.mainActivities}
              onChange={(e) => {
                onChange({
                  target: {
                    name: `mainActivities-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                });
              }}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          </InfoRow>
          <InfoRow
            title="Position (i.e. director, beneficial owner)"
            lessPadding
          >
            <InputText
              name={`position-${affilatedCompany?.id}`}
              placeholder="Enter position..."
              value={formValues?.position}
              size={InputSize.SMALL}
              onChange={(e) =>
                onChange({
                  target: {
                    name: `position-${affilatedCompany?.id}`,
                    value: e.target.value,
                  },
                })
              }
              className="borderless"
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
            />
          </InfoRow>
          <InfoRow title="Percentage of ownership (%)" lessPadding>
            <InputNumber
              name={`percentageOfOwnership-${affilatedCompany?.id}`}
              placeholder="Enter percent..."
              value={formValues?.percentageOfOwnership}
              size={InputSize.SMALL}
              onChange={(e) => {
                let nextValue = e.target.value;

                if (Number(e.target.value) < 0) {
                  nextValue = '0';
                } else if (Number(e.target.value) > 100) {
                  nextValue = '100';
                }

                onChange({
                  target: {
                    name: `percentageOfOwnership-${affilatedCompany?.id}`,
                    value: nextValue,
                  },
                });
              }}
              className="borderless"
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
            />
          </InfoRow>
        </>
      )}
    </div>
  );
};
export default AffilatedCompany;
